import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import Navbar from "../components/Navbar";
import AIChatbot from "../components/AIChatbot";
import ApplicationProcessTimeline from "../components/ApplicationProcessTimeline";
import { getRegions, getProvinces, getCitiesByRegion, getCitiesByProvince, getBarangays } from "../utils/philippineAddress";
import { genApplicationId, calcAge, BUNDLED_JOBS } from "../utils/helpers";
import { fixGrammar, notifyAdminOfApplication, notifyStatus } from "../utils/api";

const PICKUP_ADDR = process.env.REACT_APP_PICKUP_ADDRESS || "Don Gil Garcia Street, Capitol Site, Cebu City";
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const initialForm = {
  firstName: "", middleName: "", lastName: "",
  contactNumber: "", email: "",
  address: { region: "", regionCode: "", province: "", provinceCode: "", city: "", cityCode: "", barangay: "", exact: "", zip: "" },
  professionalObjective: "",
  birthdate: "", placeOfBirth: "", age: "", gender: "", civilStatus: "", citizenship: "Filipino", languages: [],
  // Month/year the applicant last graduated — basis of the work experience start date on generated documents
  lastGraduation: { month: "", year: "" },
  education: {
    elementary: { schoolName: "", address: "", yearGraduated: "", monthGraduated: "" },
    secondary:  { schoolName: "", address: "", yearGraduated: "", monthGraduated: "" },
    college:    { schoolName: "", address: "", yearGraduated: "", monthGraduated: "", course: "" }
  },
  skills: [],
  workExperience: [],
  characterReferences: [{ name: "", position: "", company: "", contact: "" }]
};

export default function ApplicationForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [skillInput, setSkillInput] = useState("");
  const [langInput, setLangInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fixingGrammar, setFixingGrammar] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { appId }
  const [error, setError] = useState("");

  // Job endorsement selection + pickup schedule (collected upfront, no separate acceptance step)
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [pickupDateTime, setPickupDateTime] = useState("");
  const allJobsSelected = BUNDLED_JOBS.every((j) => selectedJobs.includes(j));
  const toggleJobBundle = () => setSelectedJobs(allJobsSelected ? [] : [...BUNDLED_JOBS]);

  // Philippines address dropdowns
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [barangays, setBarangays] = useState([]);

  useEffect(() => {
    (async () => {
      try { setRegions(await getRegions()); } catch (e) { console.error(e); }
    })();
  }, []);

  const setAddr = (field, val) => setForm({ ...form, address: { ...form.address, [field]: val } });

  const onRegionChange = async (e) => {
    const opt = regions.find((r) => r.code === e.target.value);
    setAddr("regionCode", opt?.code || "");
    setForm((f) => ({ ...f, address: { ...f.address, region: opt?.name || "", regionCode: opt?.code || "", province: "", provinceCode: "", city: "", cityCode: "", barangay: "" }}));
    setProvinces([]); setCities([]); setBarangays([]);
    if (!opt) return;
    try {
      const ps = await getProvinces(opt.code);
      if (ps && ps.length > 0) setProvinces(ps);
      else {
        // NCR has no provinces — load cities directly
        const cs = await getCitiesByRegion(opt.code);
        setCities(cs);
      }
    } catch (e) { console.error(e); }
  };

  const onProvinceChange = async (e) => {
    const opt = provinces.find((p) => p.code === e.target.value);
    setForm((f) => ({ ...f, address: { ...f.address, province: opt?.name || "", provinceCode: opt?.code || "", city: "", cityCode: "", barangay: "" }}));
    setCities([]); setBarangays([]);
    if (!opt) return;
    try { setCities(await getCitiesByProvince(opt.code)); } catch (e) { console.error(e); }
  };

  const onCityChange = async (e) => {
    const opt = cities.find((c) => c.code === e.target.value);
    setForm((f) => ({ ...f, address: { ...f.address, city: opt?.name || "", cityCode: opt?.code || "", barangay: "" }}));
    setBarangays([]);
    if (!opt) return;
    try { setBarangays(await getBarangays(opt.code)); } catch (e) { console.error(e); }
  };

  const handlePhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const addSkill = () => {
    const v = skillInput.trim();
    if (v && !form.skills.includes(v)) setForm({ ...form, skills: [...form.skills, v] });
    setSkillInput("");
  };
  const removeSkill = (s) => setForm({ ...form, skills: form.skills.filter((x) => x !== s) });

  const addLanguage = () => {
    const v = langInput.trim();
    if (v && !form.languages.includes(v)) setForm({ ...form, languages: [...form.languages, v] });
    setLangInput("");
  };

  const addWork = () => setForm({ ...form, workExperience: [...form.workExperience, { company: "", jobRole: "", startDate: "", endDate: "" }]});
  const updateWork = (i, k, v) => {
    const updated = [...form.workExperience]; updated[i] = { ...updated[i], [k]: v };
    setForm({ ...form, workExperience: updated });
  };
  const removeWork = (i) => setForm({ ...form, workExperience: form.workExperience.filter((_, idx) => idx !== i) });

  const addRef = () => setForm({ ...form, characterReferences: [...form.characterReferences, { name: "", position: "", company: "", contact: "" }]});
  const updateRef = (i, k, v) => {
    const updated = [...form.characterReferences]; updated[i] = { ...updated[i], [k]: v };
    setForm({ ...form, characterReferences: updated });
  };
  const removeRef = (i) => setForm({ ...form, characterReferences: form.characterReferences.filter((_, idx) => idx !== i) });

  const handleFixGrammar = async () => {
    if (!form.professionalObjective.trim()) return;
    setFixingGrammar(true);
    try {
      const r = await fixGrammar(form.professionalObjective);
      if (r?.fixed) setForm({ ...form, professionalObjective: r.fixed });
      else setError("AI fix unavailable right now. Please review manually.");
    } catch { setError("AI service unavailable."); }
    setFixingGrammar(false);
  };

  const validate = () => {
    if (selectedJobs.length === 0) return "Please select the job endorsement package.";
    if (!form.firstName || !form.lastName) return "First & Last name required.";
    if (!form.contactNumber) return "Contact number required.";
    if (!form.email) return "Email required.";
    if (!form.address.region || !form.address.city) return "Region & city required.";
    if (!form.birthdate) return "Date of birth required.";
    if (!form.professionalObjective.trim()) return "Professional objective is required.";
    if (!form.lastGraduation.month || !form.lastGraduation.year) return "Please enter the month and year you last graduated.";
    if (!photoFile) return "Please attach your 2x2 photo.";
    if (!pickupDateTime) return "Please select your preferred pickup date and time.";
    return null;
  };

  const submit = async () => {
    setError("");
    const err = validate();
    if (err) { setError(err); window.scrollTo({ top: 0, behavior: "smooth" }); return; }

    setSubmitting(true);
    try {
      const applicationId = genApplicationId();

      // Upload photo
      let photoUrl = "";
      if (photoFile) {
        const r = ref(storage, `photos/${applicationId}-${photoFile.name}`);
        await uploadBytes(r, photoFile);
        photoUrl = await getDownloadURL(r);
      }

      const data = {
        ...form,
        applicationId,
        age: calcAge(form.birthdate),
        photoUrl,
        status: "pending",
        selectedJobs,
        claimDate: pickupDateTime,
        endorsementStatus: "processing",
        paymentStatus: "pending_collection",
        timeline: [
          { stage: "endorsement_requested", label: "Endorsement Requested", date: new Date().toISOString(), done: true, meta: { jobs: selectedJobs } }
        ],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, "applicants"), data);
      const fullName = `${form.firstName} ${form.lastName}`;
      try { await notifyAdminOfApplication(fullName, applicationId); } catch (e) { console.error(e); }
      try {
        await notifyStatus(form.contactNumber, form.email, form.firstName,
          `We received your Endorsement Request (ID: ${applicationId}). Save this ID to track your document status request. The agency will notify you via SMS/email once your documents are ready to receive.`);
      } catch (e) { console.error(e); }

      setSubmitted({ appId: applicationId });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
      setError("Submission failed. Please try again.");
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <>
        <Navbar />
        <div className="page">
          <div className="container-narrow">
            <div className="card card-elevated text-center" style={{ padding: 48 }}>
              <div style={{ fontSize: 64, color: "var(--success)" }}><i className="ti ti-circle-check"></i></div>
              <h1 className="page-title">Endorsement Requested!</h1>
              <p className="text-muted mb-24">Your request has been received. The agency will notify you via SMS/email once your documents are ready to receive.</p>
              <div style={{ padding: 20, background: "var(--cream-warm)", borderRadius: "var(--radius-md)", marginBottom: 24 }}>
                <div style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-600)" }}>Your Endorsement ID</div>
                <div className="mono" style={{ fontSize: 28, fontWeight: 700, color: "var(--gold-700)", margin: "8px 0" }}>{submitted.appId}</div>
                <div style={{ fontSize: 13, color: "var(--ink-500)" }}>Save this ID to track your document status request.</div>
              </div>
              <button className="btn btn-primary" onClick={() => navigate("/")}><i className="ti ti-home"></i> Back to Home</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="page">
        <div className="container-narrow">
          <div className="page-header">
            <h1 className="page-title">Request Endorsement</h1>
            <p className="page-subtitle">Select your job endorsement package, then complete your information below. Fields marked with * are required.</p>
          </div>
          {error && <div className="alert alert-error"><i className="ti ti-alert-circle"></i> {error}</div>}

          {/* Job endorsement package selection */}
          <section className="card mb-24">
            <h2 className="section-title"><i className="ti ti-briefcase"></i> Select Job Endorsement Package</h2>
            <label onClick={toggleJobBundle} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: 14, border: `2px solid ${allJobsSelected ? "var(--gold-500)" : "var(--ink-200)"}`, borderRadius: 10, background: allJobsSelected ? "var(--gold-50)" : "white", cursor: "pointer" }}>
              <input type="checkbox" checked={allJobsSelected} onChange={() => {}} style={{ marginTop: 3, flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 700 }}>Japan Skills Worker Bundle</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                  {BUNDLED_JOBS.map((j) => <span key={j} style={{ fontSize: 12, padding: "2px 10px", background: allJobsSelected ? "var(--gold-200)" : "var(--cream-warm)", borderRadius: 999 }}>{j}</span>)}
                </div>
              </div>
            </label>
          </section>

          <ApplicationProcessTimeline />

          {/* Section 1: Personal Info */}
          <section className="card mb-24">
            <h2 className="section-title"><span className="step">1</span> Personal Information</h2>
            <div className="form-row cols-3">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input className="form-control" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Middle Name</label>
                <input className="form-control" value={form.middleName} onChange={(e) => setForm({ ...form, middleName: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name *</label>
                <input className="form-control" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
              </div>
            </div>
            <div className="form-row cols-2">
              <div className="form-group">
                <label className="form-label">Contact Number *</label>
                <input className="form-control" placeholder="09XXXXXXXXX" value={form.contactNumber} onChange={(e) => setForm({ ...form, contactNumber: e.target.value })} />
                <small style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, fontSize: 12, color: "var(--info)", fontWeight: 500 }}>
                  <i className="ti ti-device-mobile"></i>
                  Globe network number only (e.g. 0917, 0926, 0935, 0936, 0975, 0976) — SMS notifications will only be delivered to Globe numbers.
                </small>
              </div>
              <div className="form-group">
                <label className="form-label">Gmail / Email *</label>
                <input className="form-control" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
          </section>

          {/* Section 2: Address */}
          <section className="card mb-24">
            <h2 className="section-title"><span className="step">2</span> Address</h2>
            <div className="form-row cols-2">
              <div className="form-group">
                <label className="form-label">Region *</label>
                <select className="form-control" value={form.address.regionCode} onChange={onRegionChange}>
                  <option value="">— Select Region —</option>
                  {regions.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Province</label>
                <select className="form-control" value={form.address.provinceCode} onChange={onProvinceChange} disabled={!provinces.length}>
                  <option value="">— Select Province —</option>
                  {provinces.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row cols-2">
              <div className="form-group">
                <label className="form-label">City / Municipality *</label>
                <select className="form-control" value={form.address.cityCode} onChange={onCityChange} disabled={!cities.length}>
                  <option value="">— Select City —</option>
                  {cities.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Barangay</label>
                <select className="form-control" value={form.address.barangay} onChange={(e) => setAddr("barangay", e.target.value)} disabled={!barangays.length}>
                  <option value="">— Select Barangay —</option>
                  {barangays.map((b) => <option key={b.code} value={b.name}>{b.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row cols-2">
              <div className="form-group">
                <label className="form-label">Exact Address (Street, Lot, etc.)</label>
                <input className="form-control" value={form.address.exact} onChange={(e) => setAddr("exact", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Zip Code</label>
                <input className="form-control" value={form.address.zip} onChange={(e) => setAddr("zip", e.target.value)} />
              </div>
            </div>
          </section>

          {/* Section 3: Professional Objective */}
          <section className="card mb-24">
            <h2 className="section-title"><span className="step">3</span> Professional Objective</h2>
            <p className="text-muted mb-16" style={{ fontSize: 13 }}>Describe your career goals.</p>
            <div className="form-group">
              <textarea className="form-control" rows={4}
                value={form.professionalObjective}
                onChange={(e) => setForm({ ...form, professionalObjective: e.target.value })}
                placeholder="My objective is to secure a position where I can apply my skills and contribute meaningfully..." />
              <div className="mt-8" style={{ textAlign: "right" }}>
                <button className="btn btn-outline" onClick={handleFixGrammar} disabled={fixingGrammar || !form.professionalObjective.trim()} type="button">
                  {fixingGrammar ? <><span className="spinner" style={{ borderTopColor: "var(--gold-500)" }}></span> Fixing...</> : <><i className="ti ti-wand"></i> Fix Grammar with AI</>}
                </button>
              </div>
            </div>
          </section>

          {/* Section 4: Demographics */}
          <section className="card mb-24">
            <h2 className="section-title"><span className="step">4</span> Personal Details</h2>
            <div className="form-row cols-3">
              <div className="form-group">
                <label className="form-label">Date of Birth *</label>
                <input className="form-control" type="date" value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value, age: calcAge(e.target.value) })} />
              </div>
              <div className="form-group">
                <label className="form-label">Place of Birth</label>
                <input className="form-control" value={form.placeOfBirth} onChange={(e) => setForm({ ...form, placeOfBirth: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Age</label>
                <input className="form-control" value={form.age || calcAge(form.birthdate)} readOnly />
              </div>
            </div>
            <div className="form-row cols-3">
              <div className="form-group">
                <label className="form-label">Gender</label>
                <select className="form-control" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                  <option value="">— Select —</option><option>Male</option><option>Female</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Civil Status</label>
                <select className="form-control" value={form.civilStatus} onChange={(e) => setForm({ ...form, civilStatus: e.target.value })}>
                  <option value="">— Select —</option><option>Single</option><option>Married</option><option>Widowed</option><option>Separated</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Citizenship</label>
                <input className="form-control" value={form.citizenship} onChange={(e) => setForm({ ...form, citizenship: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Languages Spoken</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="form-control" placeholder="e.g., English, Filipino, Cebuano" value={langInput} onChange={(e) => setLangInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLanguage())} />
                <button className="btn btn-outline" type="button" onClick={addLanguage}><i className="ti ti-plus"></i> Add</button>
              </div>
              <div className="tag-list">
                {form.languages.map((l) => (
                  <span key={l} className="tag">{l}<i className="ti ti-x tag-remove" onClick={() => setForm({ ...form, languages: form.languages.filter((x) => x !== l) })}></i></span>
                ))}
              </div>
            </div>
          </section>

          {/* Section 5: Education */}
          <section className="card mb-24">
            <h2 className="section-title"><span className="step">5</span> Educational Background</h2>
            <div style={{ padding: 16, marginBottom: 20, background: "var(--gold-50)", border: "1px solid var(--gold-200)", borderRadius: 10 }}>
              <h4 style={{ marginBottom: 4, fontFamily: "var(--font-body)", fontSize: 15, color: "var(--gold-700)" }}>When did you last graduate? *</h4>
              <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>Enter the month and year you finished school and started working. This is used as the start date of your work experience.</p>
              <div className="form-row cols-2">
                <div className="form-group">
                  <label className="form-label">Month *</label>
                  <select className="form-control" value={form.lastGraduation.month} onChange={(e) => setForm({ ...form, lastGraduation: { ...form.lastGraduation, month: e.target.value }})}>
                    <option value="">—</option>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Year *</label>
                  <input type="number" className="form-control" placeholder="e.g. 2022" value={form.lastGraduation.year} onChange={(e) => setForm({ ...form, lastGraduation: { ...form.lastGraduation, year: e.target.value }})} />
                </div>
              </div>
            </div>
            {["elementary", "secondary", "college"].map((level) => (
              <div key={level} style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px dashed var(--ink-200)" }}>
                <h4 style={{ textTransform: "capitalize", marginBottom: 12, fontFamily: "var(--font-body)", fontSize: 15, color: "var(--gold-700)" }}>
                  {level} {level === "college" && <span style={{ fontSize: 12, color: "var(--ink-400)", fontWeight: 400 }}>(if applicable)</span>}
                </h4>
                <div className="form-row cols-2">
                  <div className="form-group">
                    <label className="form-label">School Name</label>
                    <input className="form-control" value={form.education[level].schoolName} onChange={(e) => setForm({ ...form, education: { ...form.education, [level]: { ...form.education[level], schoolName: e.target.value }}})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Address</label>
                    <input className="form-control" value={form.education[level].address} onChange={(e) => setForm({ ...form, education: { ...form.education, [level]: { ...form.education[level], address: e.target.value }}})} />
                  </div>
                </div>
                <div className="form-row cols-3">
                  <div className="form-group">
                    <label className="form-label">Year Graduated</label>
                    <input type="number" className="form-control" value={form.education[level].yearGraduated} onChange={(e) => setForm({ ...form, education: { ...form.education, [level]: { ...form.education[level], yearGraduated: e.target.value }}})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Month Graduated</label>
                    <select className="form-control" value={form.education[level].monthGraduated} onChange={(e) => setForm({ ...form, education: { ...form.education, [level]: { ...form.education[level], monthGraduated: e.target.value }}})}>
                      <option value="">—</option>
                      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  {level === "college" && (
                    <div className="form-group">
                      <label className="form-label">Course</label>
                      <input className="form-control" value={form.education.college.course || ""} onChange={(e) => setForm({ ...form, education: { ...form.education, college: { ...form.education.college, course: e.target.value }}})} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </section>

          {/* Section 6: Skills */}
          <section className="card mb-24">
            <h2 className="section-title"><span className="step">6</span> Skills</h2>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="form-control" placeholder="Add a skill..." value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())} />
              <button className="btn btn-primary" type="button" onClick={addSkill}><i className="ti ti-plus"></i> Add</button>
            </div>
            <div className="tag-list">
              {form.skills.map((s) => (
                <span key={s} className="tag">{s}<i className="ti ti-x tag-remove" onClick={() => removeSkill(s)}></i></span>
              ))}
            </div>
          </section>

          {/* Section 7: Work Experience */}
          <section className="card mb-24">
            <h2 className="section-title"><span className="step">7</span> Work Experience</h2>
            {form.workExperience.map((w, i) => (
              <div key={i} style={{ padding: 16, background: "var(--cream-warm)", borderRadius: "var(--radius-md)", marginBottom: 12 }}>
                <div className="flex-between mb-8">
                  <strong>Experience #{i + 1}</strong>
                  <button className="btn btn-ghost" type="button" onClick={() => removeWork(i)}><i className="ti ti-trash"></i></button>
                </div>
                <div className="form-row cols-2">
                  <div className="form-group"><label className="form-label">Company</label><input className="form-control" value={w.company} onChange={(e) => updateWork(i, "company", e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Job Role</label><input className="form-control" value={w.jobRole} onChange={(e) => updateWork(i, "jobRole", e.target.value)} /></div>
                </div>
                <div className="form-row cols-2">
                  <div className="form-group"><label className="form-label">Start Date</label><input type="date" className="form-control" value={w.startDate} onChange={(e) => updateWork(i, "startDate", e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">End Date (leave blank if Present)</label><input type="date" className="form-control" value={w.endDate} onChange={(e) => updateWork(i, "endDate", e.target.value)} /></div>
                </div>
              </div>
            ))}
            <button className="btn btn-outline" type="button" onClick={addWork}><i className="ti ti-plus"></i> Add Work Experience</button>
          </section>

          {/* Section 8: Character References */}
          <section className="card mb-24">
            <h2 className="section-title"><span className="step">8</span> Character References</h2>
            {form.characterReferences.map((r, i) => (
              <div key={i} style={{ padding: 16, background: "var(--cream-warm)", borderRadius: "var(--radius-md)", marginBottom: 12 }}>
                <div className="flex-between mb-8">
                  <strong>Reference #{i + 1}</strong>
                  {form.characterReferences.length > 1 && (
                    <button className="btn btn-ghost" type="button" onClick={() => removeRef(i)}><i className="ti ti-trash"></i></button>
                  )}
                </div>
                <div className="form-row cols-2">
                  <div className="form-group"><label className="form-label">Full Name</label><input className="form-control" value={r.name} onChange={(e) => updateRef(i, "name", e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Position</label><input className="form-control" value={r.position} onChange={(e) => updateRef(i, "position", e.target.value)} /></div>
                </div>
                <div className="form-row cols-2">
                  <div className="form-group"><label className="form-label">Company/Organization</label><input className="form-control" value={r.company} onChange={(e) => updateRef(i, "company", e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Contact Number</label><input className="form-control" value={r.contact} onChange={(e) => updateRef(i, "contact", e.target.value)} /></div>
                </div>
              </div>
            ))}
            <button className="btn btn-outline" type="button" onClick={addRef}><i className="ti ti-plus"></i> Add Reference</button>
          </section>

          {/* Section 9: 2x2 Photo */}
          <section className="card mb-24">
            <h2 className="section-title"><span className="step">9</span> 2x2 Photo (White Background)</h2>
            <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ width: 144, height: 144, border: "2px dashed var(--ink-300)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--cream-warm)" }}>
                {photoPreview ? <img src={photoPreview} alt="2x2 preview" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6 }} /> :
                  <span style={{ color: "var(--ink-400)", fontSize: 13 }}>Preview</span>}
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <input type="file" accept="image/*" onChange={handlePhoto} className="form-control" />
                <p className="text-muted mt-8" style={{ fontSize: 13 }}>Upload a recent 2x2 photo with white background and collar.</p>
              </div>
            </div>
          </section>

          {/* Schedule Pickup */}
          <section className="card mb-24">
            <h2 className="section-title"><span className="step">10</span> Schedule Pickup</h2>
            <div className="form-group">
              <label className="form-label">Preferred Pickup Date &amp; Time *</label>
              <input type="datetime-local" className="form-control" value={pickupDateTime} onChange={(e) => setPickupDateTime(e.target.value)} />
              <small className="text-muted">Pickup at: {PICKUP_ADDR}</small>
            </div>
          </section>

          {/* Payment */}
          <section className="card mb-24">
            <h2 className="section-title"><i className="ti ti-cash"></i> Payment</h2>
            <div className="alert alert-info" style={{ fontSize: 13 }}>
              <i className="ti ti-info-circle"></i> Payment is collected in cash when you receive your endorsement documents — no GCash, no advance payment. Endorsement fee: <strong>₱6,000</strong>.
            </div>
          </section>

          {/* Submit */}
          <div className="text-center mt-32">
            <button className="btn btn-primary" disabled={submitting} onClick={submit} style={{ padding: "16px 40px", fontSize: 16 }}>
              {submitting ? <><span className="spinner"></span> Submitting...</> : <><i className="ti ti-send"></i> Submit Endorsement Request</>}
            </button>
          </div>
        </div>
      </div>
      <AIChatbot />
    </>
  );
}
